import React, { useRef, useState } from "react";
import { useFavorites } from '../context/FavoritesContext';

const HotelCard = ({
    id,
    images = [],
    title = "Hotel Name",
    location = "Location",
    distance = "1.5 km",
    rating = 4.4,
    ratingCount = 241,
    ratingText = "Very Good",
    amenities = [],
    price = 800,
    originalPrice = 4210,
    discountPercent = 77,
    taxesNote = "+ ₹144 taxes & fees · per room per night",
    isSoldOut = false,
    onViewDetails = () => { },
    onBookNow = () => { },
    onFavorite = () => { },
}) => {
    const [mainImageIndex, setMainImageIndex] = useState(0);
    const [showAllAmenities, setShowAllAmenities] = useState(false);
    const { favoriteIds, toggleFavorite } = useFavorites();
    const isLiked = favoriteIds.includes(id);
    const scrollRef = useRef(null);

    const sampleImages = [
        "https://images.unsplash.com/photo-1566073771259-6a8506099945?w=800&h=600&fit=crop",
        "https://images.unsplash.com/photo-1564501049412-61c2a3083791?w=800&h=600&fit=crop",
        "https://images.unsplash.com/photo-1590490360182-c33d57733427?w=800&h=600&fit=crop",
        "https://images.unsplash.com/photo-1571896349842-33c89424de2d?w=800&h=600&fit=crop",
    ];

    const displayImages = images.length > 0 ? images : sampleImages;

    const getRatingColor = (rating) => {
        if (rating >= 4.5) return "bg-emerald-500";
        if (rating >= 4.0) return "bg-green-500";
        if (rating >= 3.5) return "bg-yellow-500";
        return "bg-orange-500";
    };

    const handleImageScroll = (e) => {
        const container = e.target;
        const imageWidth = container.offsetWidth;
        const scrollLeft = container.scrollLeft;
        const currentIndex = Math.round(scrollLeft / imageWidth);
        setMainImageIndex(currentIndex);
    };

    const scrollToImage = (index) => {
        if (scrollRef.current) {
            const imageWidth = scrollRef.current.offsetWidth;
            scrollRef.current.scrollTo({
                left: index * imageWidth,
                behavior: 'smooth'
            });
        }
    };

    const handleFavorite = async () => {
        if (!id) return;
        const success = await toggleFavorite(id);
        if (success) {
            onFavorite();
        }
    };

    return (
        <div className="bg-white overflow-hidden border-b border-gray-200 rounded-none mx-0 mb-0 lg:bg-white lg:rounded-2xl lg:mb-6 lg:shadow-md lg:hover:shadow-xl lg:transition-shadow lg:duration-300 lg:border lg:border-gray-100">
            {/* Mobile Layout */}
            <div className="block lg:hidden p-3 active:bg-gray-50 transition-colors" onClick={onViewDetails}>
                <div className="flex gap-3">
                    {/* Left: Image */}
                    <div className="relative w-[110px] h-[130px] flex-shrink-0">
                        <img
                            src={displayImages[mainImageIndex]}
                            alt={title}
                            className="w-full h-full object-cover rounded-xl"
                        />
                        {/* Top Badges */}
                        <div className="absolute top-1 left-1 flex flex-col gap-1 z-10">
                            {discountPercent > 0 && (
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-black bg-gradient-to-r from-orange-500 to-red-500 text-white shadow-sm tracking-wide">
                                    {discountPercent}% OFF
                                </span>
                            )}
                        </div>
                        {/* Favorite Button */}
                        <button
                            onClick={(e) => { e.stopPropagation(); handleFavorite(); }}
                            className="absolute top-1 right-1 p-1.5 rounded-full bg-black/20 backdrop-blur-sm z-10 active:scale-90 transition-transform"
                        >
                            <svg className={`w-3.5 h-3.5 ${isLiked ? 'text-red-500 fill-current' : 'text-white'}`} fill={isLiked ? "currentColor" : "none"} stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                            </svg>
                        </button>
                        
                        {isSoldOut && (
                            <div className="absolute inset-0 bg-white/50 backdrop-blur-[2px] rounded-xl flex items-center justify-center z-10">
                                <span className="px-2 py-1 bg-gray-900 text-white text-[10px] font-bold rounded shadow-md tracking-widest">SOLD OUT</span>
                            </div>
                        )}
                    </div>

                    {/* Right: Content */}
                    <div className="flex-1 flex flex-col justify-between py-0.5 overflow-hidden">
                        <div>
                            <div className="flex justify-between items-start">
                                <h3 className="text-[15px] font-bold text-gray-900 leading-tight line-clamp-2 capitalize">
                                    {title}
                                </h3>
                            </div>
                            
                            <div className="flex items-center text-[11px] text-gray-500 mt-1 mb-1.5 line-clamp-1">
                                <svg className="w-3 h-3 text-blue-500 mr-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                </svg>
                                <span className="truncate">{location}</span>
                            </div>

                            <div className="flex items-center space-x-1.5 mb-2">
                                <div className={`inline-flex items-center px-1.5 py-0.5 rounded text-white text-[10px] font-bold ${getRatingColor(rating)}`}>
                                    <svg className="w-2.5 h-2.5 mr-0.5" fill="currentColor" viewBox="0 0 20 20">
                                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                    </svg>
                                    {Number(rating || 0).toFixed(1)}
                                </div>
                                <span className="text-[10px] text-gray-500 font-medium">
                                    {ratingText} ({ratingCount})
                                </span>
                            </div>

                            {/* Simplified Amenities */}
                            {amenities.length > 0 && (
                                <div className="flex flex-wrap gap-1 mb-1 max-h-[20px] overflow-hidden">
                                    {amenities.slice(0, 2).map((amenity, idx) => (
                                        <span key={idx} className="text-[9px] px-1.5 py-0.5 bg-blue-50 text-blue-700 font-medium rounded border border-blue-100">
                                            {amenity.label}
                                        </span>
                                    ))}
                                    {amenities.length > 2 && (
                                        <span className="text-[9px] px-1.5 py-0.5 bg-gray-50 text-gray-600 font-medium rounded border border-gray-100">
                                            +{amenities.length - 2}
                                        </span>
                                    )}
                                </div>
                            )}
                        </div>

                        <div className="flex items-end justify-between mt-auto">
                            <div>
                                <div className="flex items-baseline gap-1">
                                    <span className="text-lg font-black text-gray-900 tracking-tight">₹{price.toLocaleString()}</span>
                                    {originalPrice && originalPrice > price && (
                                        <span className="text-[10px] text-gray-400 line-through font-medium">₹{originalPrice.toLocaleString()}</span>
                                    )}
                                </div>
                                <p className="text-[9px] text-gray-400 font-medium leading-none">{taxesNote.split('·')[0].trim()}</p>
                            </div>
                            
                            <button
                                onClick={(e) => { e.stopPropagation(); onBookNow(); }}
                                disabled={isSoldOut}
                                className={`px-3.5 py-1.5 text-[11px] font-bold text-white rounded-lg transition-all active:scale-95 ${
                                    isSoldOut ? 'bg-gray-400' : 'bg-blue-600 shadow-sm shadow-blue-600/30'
                                }`}
                            >
                                {isSoldOut ? 'Sold Out' : 'Book'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Desktop Layout */}
            <div 
                className="hidden lg:flex gap-5 p-4 cursor-pointer hover:bg-gray-50 transition-colors" 
                onClick={onViewDetails}
            >
                <div className="w-[320px] flex-shrink-0">
                    <div className="relative group overflow-hidden rounded-2xl">
                        <img
                            src={displayImages[mainImageIndex]}
                            alt={title}
                            className="w-full h-52 object-cover transition-transform duration-500 group-hover:scale-105"
                        />
                    </div>

                    {displayImages.length > 1 && (
                        <div className="flex gap-2 mt-3 overflow-x-auto pb-1 scrollbar-hide">
                            {displayImages.map((img, idx) => (
                                <img
                                    key={idx}
                                    src={img}
                                    alt={`Thumbnail ${idx + 1}`}
                                    className={`flex-shrink-0 w-16 h-12 object-cover rounded-lg cursor-pointer transition-all duration-200 border-2 ${idx === mainImageIndex
                                            ? "border-blue-500 opacity-100"
                                            : "border-transparent opacity-60 hover:opacity-100"
                                        }`}
                                    onClick={(e) => { e.stopPropagation(); setMainImageIndex(idx); }}
                                />
                            ))}
                        </div>
                    )}
                </div>

                <div className="flex-1 flex flex-col">
                    <div className="space-y-3">
                        <div>
                            <h3 className="text-xl font-semibold text-gray-900 line-clamp-2 leading-tight capitalize">
                                {title}
                            </h3>
                            <div className="flex items-center text-gray-600 text-sm mt-1">
                                <span>{location}</span>
                                {distance && (
                                    <>
                                        <span className="mx-2">•</span>
                                        <div className="flex items-center">
                                            <svg className="w-4 h-4 text-red-500 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                                            </svg>
                                            {distance}
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>

                        <div className="flex items-center gap-3">
                            <div className={`inline-flex items-center px-2 py-1 rounded-md text-white text-sm font-medium ${getRatingColor(rating)}`}>
                                <svg className="w-3.5 h-3.5 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                </svg>
                                {Number(rating || 0).toFixed(1)}
                            </div>
                            <span className="text-gray-600 text-sm">
                                ({ratingCount.toLocaleString()} reviews) • {ratingText}
                            </span>
                        </div>

                        {amenities.length > 0 && (
                            <div className="flex flex-wrap gap-x-3 gap-y-2 pt-2">
                                {amenities.slice(0, showAllAmenities ? amenities.length : 4).map((amenity, idx) => (
                                    <div key={idx} className="flex items-center text-sm text-gray-700 bg-gray-50 border border-gray-100 rounded-md px-2 py-1 shadow-sm">
                                        <svg className="w-4 h-4 mr-1.5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                        </svg>
                                        {amenity.label}
                                    </div>
                                ))}
                                {!showAllAmenities && amenities.length > 4 && (
                                    <div 
                                        onClick={(e) => { e.stopPropagation(); setShowAllAmenities(true); }}
                                        className="flex items-center bg-blue-50 border border-blue-100 rounded-md px-2 py-1 cursor-pointer hover:bg-blue-100 transition-colors"
                                    >
                                        <span className="text-xs font-bold text-blue-600">+{amenities.length - 4} more</span>
                                    </div>
                                )}
                                {showAllAmenities && amenities.length > 4 && (
                                    <div 
                                        onClick={(e) => { e.stopPropagation(); setShowAllAmenities(false); }}
                                        className="flex items-center bg-gray-50 border border-gray-200 rounded-md px-2 py-1 cursor-pointer hover:bg-gray-100 transition-colors"
                                    >
                                        <span className="text-xs font-bold text-gray-600">Show less</span>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    <div className="flex items-end justify-between pt-5 mt-5 border-t border-dashed border-gray-200">
                        <div className="flex-1">
                            <div className="flex items-baseline gap-2 mb-1">
                                <span className="text-2xl font-bold text-green-600">₹{price.toLocaleString()}</span>
                                {originalPrice && (
                                    <span className="text-lg text-red-400 line-through">₹{originalPrice.toLocaleString()}</span>
                                )}
                                {discountPercent > 0 && (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                                        {discountPercent}% OFF
                                    </span>
                                )}
                                {isSoldOut && (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-800 text-white">
                                        SOLD OUT
                                    </span>
                                )}
                            </div>
                            <p className="text-xs text-gray-500">{taxesNote}</p>
                        </div>

                        <div className="flex gap-2 ml-4">
                            <button
                                onClick={(e) => { e.stopPropagation(); handleFavorite(); }}
                                className="p-2 text-gray-600 hover:text-red-500 transition-colors duration-200"
                            >
                                <svg
                                    className={`w-5 h-5 ${isLiked ? 'text-red-500 fill-current' : ''}`}
                                    fill={isLiked ? "currentColor" : "none"}
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                                </svg>
                            </button>
                            <button
                                onClick={(e) => { e.stopPropagation(); onViewDetails(); }}
                                className="px-6 py-2.5 text-sm font-semibold text-blue-700 bg-blue-50 rounded-full hover:bg-blue-100 transition-colors duration-200 active:scale-95 whitespace-nowrap"
                            >
                                View Details
                            </button>
                            <button
                                onClick={(e) => { e.stopPropagation(); onBookNow(); }}
                                disabled={isSoldOut}
                                className={`px-8 py-2.5 text-sm font-semibold text-white rounded-full transition-all duration-200 active:scale-95 shadow-md whitespace-nowrap ${
                                    isSoldOut
                                        ? 'bg-gray-400 cursor-not-allowed shadow-none'
                                        : 'bg-blue-600 hover:bg-blue-700 shadow-blue-600/30'
                                }`}
                            >
                                {isSoldOut ? 'Sold Out' : 'Book Now'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};


export default HotelCard;
